/**
 * StepStore — per-chat live step state. rAF-batched, `useSyncExternalStore`-shaped.
 *
 * Deliberately the same contract as [ChunkBuffer](../stream/ChunkBuffer.ts)
 * (`subscribe` / `getSnapshot` / `reset` / `dispose`) and held in the same place,
 * the per-chat entry of `ChatStreamRegistry` — so a background chat accumulates
 * steps while unmounted exactly as it accumulates text, and one `dispose` tears
 * both down.
 */

import {
  cancelFrame,
  scheduleFrame
} from 'src/content/ChatV2/lib/frameScheduler';
import type { WireEnvelope } from 'src/content/ChatV2/transport/eventSchema';
import type { Step } from 'src/content/ChatV2/types/steps';

import { reconcile } from './model/reconcile';
import {
  applyEvent,
  cancelNonTerminal,
  orphanNonTerminal
} from './model/reducer';
import type {
  NormalizedEvent,
  StepKey,
  StepStoreSnapshot
} from './model/types';
import { EMPTY_SNAPSHOT } from './model/types';
import { normalizeEvent } from './wire/normalize';

export interface StepStore {
  ingest(envelope: WireEnvelope): void;
  apply(event: NormalizedEvent): void;
  settle(additionalSteps: unknown): void;
  cancel(additionalSteps: unknown): void;
  reset(): void;
  getSnapshot(): StepStoreSnapshot;
  getStep(key: StepKey): Step | undefined;
  subscribe(listener: () => void): () => void;
  subscribeStep(key: StepKey, listener: () => void): () => void;
  dispose(): void;
}

export function createStepStore(): StepStore {
  let committed: StepStoreSnapshot = EMPTY_SNAPSHOT;
  let pending: StepStoreSnapshot = EMPTY_SNAPSHOT;

  let frameHandle: number | null = null;
  let disposed = false;

  const listeners = new Set<() => void>();
  const stepListeners = new Map<StepKey, Set<() => void>>();
  const dirty = new Set<StepKey>();

  let ordinal = 0;

  const notify = () => {
    const listChanged = committed.order !== pending.order;
    const dirtyKeys = [...dirty];
    dirty.clear();
    committed = pending;

    if (listChanged) {
      for (const listener of [...listeners]) {
        listener();
      }
    }
    for (const key of dirtyKeys) {
      const set = stepListeners.get(key);
      if (!set) {
        continue;
      }
      for (const listener of [...set]) {
        listener();
      }
    }
  };

  const flushNow = () => {
    if (frameHandle !== null) {
      cancelFrame(frameHandle);
      frameHandle = null;
    }
    if (pending === committed) {
      return;
    }
    notify();
  };

  const schedule = () => {
    if (frameHandle === null) {
      frameHandle = scheduleFrame(() => {
        frameHandle = null;
        if (pending !== committed) {
          notify();
        }
      });
    }
  };

  const markDirty = (before: StepStoreSnapshot, after: StepStoreSnapshot) => {
    for (const [key, step] of after.byKey) {
      if (before.byKey.get(key) !== step) {
        dirty.add(key);
      }
    }
    for (const key of before.byKey.keys()) {
      if (!after.byKey.has(key)) {
        dirty.add(key);
      }
    }
  };

  const applyNormalized = (event: NormalizedEvent) => {
    if (disposed) {
      return;
    }
    const next = applyEvent(pending, event);
    if (next === pending) {
      return;
    }
    markDirty(pending, next);
    pending = next;
    schedule();
  };

  return {
    ingest(envelope: WireEnvelope) {
      if (disposed) {
        return;
      }
      applyNormalized(normalizeEvent(envelope, { ordinal: ordinal++ }));
    },

    apply: applyNormalized,

    settle(additionalSteps) {
      if (disposed) {
        return;
      }
      const reconciled = reconcile(pending, additionalSteps);
      const next = orphanNonTerminal(reconciled);
      markDirty(pending, next);
      pending = next;
      flushNow();
    },

    cancel(additionalSteps) {
      if (disposed) {
        return;
      }
      const reconciled = reconcile(pending, additionalSteps);
      const next = cancelNonTerminal(reconciled);
      markDirty(pending, next);
      pending = next;
      flushNow();
    },

    reset() {
      if (disposed) {
        return;
      }
      if (pending === EMPTY_SNAPSHOT && committed === EMPTY_SNAPSHOT) {
        return;
      }
      markDirty(pending, EMPTY_SNAPSHOT);
      pending = EMPTY_SNAPSHOT;
      flushNow();
    },

    getSnapshot() {
      return committed;
    },

    getStep(key) {
      return committed.byKey.get(key);
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    subscribeStep(key, listener) {
      let set = stepListeners.get(key);
      if (!set) {
        set = new Set();
        stepListeners.set(key, set);
      }
      set.add(listener);
      return () => {
        const current = stepListeners.get(key);
        if (!current) {
          return;
        }
        current.delete(listener);
        if (current.size === 0) {
          stepListeners.delete(key);
        }
      };
    },

    dispose() {
      if (disposed) {
        return;
      }
      disposed = true;
      if (frameHandle !== null) {
        cancelFrame(frameHandle);
        frameHandle = null;
      }
      listeners.clear();
      stepListeners.clear();
      dirty.clear();
      committed = EMPTY_SNAPSHOT;
      pending = EMPTY_SNAPSHOT;
    }
  };
}
